import { useCallback, useRef, useState, type DragEvent } from 'react';

interface Props {
  onFiles: (files: File[]) => void;
  disabled?: boolean;
  maxFileBytes: number;
  /** `null` when this account has no cap on how many files it may select. */
  maxFilesPerBatch: number | null;
}

/* --- Folder-drop support: the DataTransfer entry API, kept minimal. -------- */

interface FileSystemEntryLike {
  isFile: boolean;
  isDirectory: boolean;
  file(cb: (file: File) => void, err?: (e: unknown) => void): void;
  createReader(): { readEntries(cb: (entries: FileSystemEntryLike[]) => void, err?: (e: unknown) => void): void };
}

async function readEntry(entry: FileSystemEntryLike, out: File[], depth = 0): Promise<void> {
  if (depth > 5 || out.length > 500) return;

  if (entry.isFile) {
    const file = await new Promise<File | null>((resolve) => entry.file(resolve, () => resolve(null)));
    if (file) out.push(file);
    return;
  }

  if (entry.isDirectory) {
    const reader = entry.createReader();
    // readEntries returns at most 100 at a time, so keep calling until empty.
    for (;;) {
      const batch = await new Promise<FileSystemEntryLike[]>((resolve) =>
        reader.readEntries(resolve, () => resolve([])),
      );
      if (batch.length === 0) break;
      for (const child of batch) await readEntry(child, out, depth + 1);
    }
  }
}

async function filesFromDrop(dataTransfer: DataTransfer): Promise<File[]> {
  const items = Array.from(dataTransfer.items ?? []);
  const entries = items
    .map((item) => (item as unknown as { webkitGetAsEntry?: () => FileSystemEntryLike | null }).webkitGetAsEntry?.())
    .filter((entry): entry is FileSystemEntryLike => Boolean(entry));

  if (entries.length === 0) return Array.from(dataTransfer.files ?? []);

  const collected: File[] = [];
  for (const entry of entries) await readEntry(entry, collected);
  return collected;
}

export function Dropzone({ onFiles, disabled, maxFileBytes, maxFilesPerBatch }: Props) {
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const depth = useRef(0);

  const handle = useCallback((files: File[]) => {
    if (files.length > 0) onFiles(files);
  }, [onFiles]);

  const onDrop = useCallback(
    async (event: DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      depth.current = 0;
      setDragging(false);
      if (disabled) return;
      handle(await filesFromDrop(event.dataTransfer));
    },
    [disabled, handle],
  );

  const maxMb = Math.round(maxFileBytes / (1024 * 1024));

  return (
    <div
      className={'dropzone' + (dragging ? ' is-dragging' : '')}
      role="button"
      tabIndex={0}
      aria-disabled={disabled}
      onClick={() => !disabled && inputRef.current?.click()}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          if (!disabled) inputRef.current?.click();
        }
      }}
      onDragEnter={(event) => {
        event.preventDefault();
        depth.current += 1;
        setDragging(true);
      }}
      onDragOver={(event) => event.preventDefault()}
      onDragLeave={(event) => {
        event.preventDefault();
        depth.current -= 1;
        if (depth.current <= 0) setDragging(false);
      }}
      onDrop={onDrop}
    >
      <div className="dropzone-icon" aria-hidden="true">
        🖼️
      </div>
      <div className="dropzone-title">Drop HEIC photos here</div>
      <div className="dropzone-hint">
        or click to browse.{' '}
        {maxFilesPerBatch === null
          ? 'Any number of files, '
          : 'Up to ' + maxFilesPerBatch.toLocaleString() + ' files, '}
        {maxMb} MB each.
      </div>

      <input
        ref={inputRef}
        type="file"
        className="visually-hidden"
        multiple
        accept=".heic,.heif,image/heic,image/heif"
        disabled={disabled}
        onChange={(event) => {
          handle(Array.from(event.target.files ?? []));
          event.target.value = ''; // let the same file be picked twice in a row
        }}
      />
    </div>
  );
}
